import React, { useEffect, useState, useCallback, lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import HomePage from './HomePage';
import AppShell from './Layout/AppShell';
import TopBar from './Layout/TopBar';
import SideNav from './Layout/SideNav';
import TrendingAside from './Layout/TrendingAside';
import { Outlet } from 'react-router-dom';
import { Drawer } from '@mui/material';
import { AuthProvider } from './provider/AuthProvider';
import { Box, CircularProgress, Tooltip, Fab, Dialog, DialogContent, useMediaQuery, useTheme, Typography, List, ListItem, Chip, Snackbar, Alert } from '@mui/material';
import { CacheProvider } from './provider/CacheProvider';
import ErrorBoundary from './ErrorBoundary';
import { LoadingProvider } from './LoadingProvider';
import { UserDataProvider } from './provider/UserDataProvider';
import { NotificationsProvider } from './provider/NotificationsProvider';
import { FollowsProvider } from './provider/FollowsProvider';
import { CompatibilityProvider } from './provider/CompatibilityProvider';
import { ColorModeProvider } from './provider/ColorModeProvider';
import KonamiEasterEgg from './components/KonamiEasterEgg';
import PowerHourBanner from './components/PowerHourBanner';
import NotFoundPage from './NotFoundPage';
import InstallPrompt from './components/InstallPrompt';
import FirstPostCelebration from './components/FirstPostCelebration';
import TimeDeltaFloater from './components/TimeDeltaFloater';
import MobileTabBar from './components/MobileTabBar';
import DeathWarningModal from './components/DeathWarningModal';
import CRTEffect from './components/CRTEffect';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import SubmitPostComponent from './SubmitPostComponent/SubmitPostComponent';
import { AuthContext, MyContext } from './context/AuthContext';
import { useLocation } from 'react-router-dom';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useReferralClaim } from './hooks/useReferralClaim';
import { hapticHeavy } from './lib/haptics';

// Cold-path routes — code-split per Phase 4 P-02. Each `lazy()` call becomes
// a separate webpack chunk, shaving ~80-120 KB gzipped off the initial bundle.
// The <Suspending> wrapper already provides a spinner fallback + error boundary.
const UserPage = lazy(() => import('./userPageComponent/userPageComponent'));
const PostPage = lazy(() => import('./postComponent/PostPageComponent'));
const WelcomePage = lazy(() => import('./LoginPage/WelcomePage'));
const AboutPage = lazy(() => import('./LoginPage/AboutPage'));
const GraveyardPage = lazy(() => import('./GraveyardPage'));
const TombPage = lazy(() => import('./TombPage'));
const LoginPanel = lazy(() => import('./LoginPage/loginComponent'));
const SignUpPage = lazy(() => import('./LoginPage/SignUpPage'));
const LeaderboardPage = lazy(() => import('./Leaderboard/LeaderboardPage'));
const FeedsListPage = lazy(() => import('./FeedComponent/FeedsListPage'));
const FeedPage = lazy(() => import('./FeedComponent/FeedPage'));
const NotificationList = lazy(() => import('./NotificationComponent/NotificationsList'));
const SettingsPage = lazy(() => import('./SettingsPage/SettingsPage'));
const FollowPage = lazy(() => import('./Follows/FollowPage'));
const BookmarksPage = lazy(() => import('./BookmarksPage'));
const InvitePage = lazy(() => import('./InvitePage'));
const StorePage = lazy(() => import('./StorePage'));
const EmbedPostPage = lazy(() => import('./EmbedPostPage'));

function MobileComposeFab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const userRecord = React.useContext<MyContext>(AuthContext).userRecord;
  const [open, setOpen] = useState(false);

  // Also open when the tab bar compose button fires the event.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('wipe-mobile-compose', handler);
    return () => window.removeEventListener('wipe-mobile-compose', handler);
  }, []);

  // Only render the fixed FAB when we're not on the home page; tab bar
  // provides the compose affordance there.
  if (!isMobile || !userRecord) return null;
  const showFab = location.pathname !== '/';
  if (!showFab && !open) return null;

  return (
    <>
      {showFab && (
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 82, // sit above the mobile tab bar
            right: 24,
            zIndex: 1200,
          }}
        >
          <AddIcon />
        </Fab>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, m: 2 } }}
      >
        <DialogContent sx={{ pt: 2 }}>
          <SubmitPostComponent
            objectType="posts"
            onSubmit={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

const SHORTCUTS = [
  { keys: ['n', 'c'], label: 'New post' },
  { keys: ['/'], label: 'Focus search' },
  { keys: ['j'], label: 'Next post' },
  { keys: ['k'], label: 'Previous post' },
  { keys: ['g', 'g'], label: 'Top of feed' },
  { keys: ['⇧', 'g'], label: 'Bottom of feed' },
  { keys: ['⌘', '↵'], label: 'Quick submit composer' },
  { keys: ['?'], label: 'Show shortcuts' },
];

function KeyboardShortcutsManager() {
  const location = useLocation();
  const userRecord = React.useContext<MyContext>(AuthContext).userRecord;
  const [composeOpen, setComposeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const onCompose = useCallback(() => {
    if (!userRecord) return;
    if (location.pathname === '/') {
      // On home page — focus the composer textarea if present
      const ta = document.querySelector<HTMLTextAreaElement>('textarea');
      ta?.focus();
      return;
    }
    setComposeOpen(true);
  }, [userRecord, location.pathname]);

  const onFocusSearch = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>('input[type="text"], input[placeholder*="earch"]');
    input?.focus();
  }, []);

  const onHelp = useCallback(() => setHelpOpen((p) => !p), []);

  useKeyboardShortcuts({ onCompose, onFocusSearch, onHelp, enabled: true });

  if (!userRecord) return null;

  return (
    <>
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, m: 2 } }}>
        <DialogContent sx={{ pt: 2 }}>
          <SubmitPostComponent objectType="posts" onSubmit={() => setComposeOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1, mb: 2 }}>
            KEYBOARD SHORTCUTS
          </Typography>
          <List disablePadding>
            {SHORTCUTS.map(({ keys, label }) => (
              <ListItem key={label} disablePadding sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {keys.map((k) => (
                    <Chip key={k} label={k} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', height: 22 }} />
                  ))}
                </Box>
              </ListItem>
            ))}
          </List>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
            Shortcuts are disabled while typing in a text field.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
}

function ReferralClaimListener() {
  useReferralClaim();
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ bonusSeconds: number }>;
      const days = Math.round((ce.detail?.bonusSeconds ?? 0) / 86400);
      setToast(`🎁 Referral bonus: +${days} days added to your wallet!`);
      hapticHeavy();
    };
    window.addEventListener('wipe-referral-claimed', handler);
    return () => window.removeEventListener('wipe-referral-claimed', handler);
  }, []);

  return (
    <Snackbar
      open={!!toast}
      autoHideDuration={6000}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
        {toast}
      </Alert>
    </Snackbar>
  );
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <Tooltip title="Back to top" placement="left">
      <Fab
        size="small"
        color="primary"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
          opacity: 0.85,
          '&:hover': { opacity: 1 },
          transition: 'opacity 0.2s',
        }}
      >
        <KeyboardArrowUpIcon />
      </Fab>
    </Tooltip>
  );
}

const Suspending = ({ children }: { children: React.ReactNode }) => (
  <React.Suspense
    fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    }
  >
    <ErrorBoundary>{children}</ErrorBoundary>
  </React.Suspense>
);

const routes = [
  { path: '/',                 element: <Suspending><HomePage /></Suspending> },
  { path: '/leaderboard',      element: <Suspending><LeaderboardPage /></Suspending> },
  { path: '/list/feeds',       element: <Suspending><FeedsListPage /></Suspending> },
  { path: '/feeds/:feedId',    element: <Suspending><FeedPage /></Suspending> },
  { path: '/notifications',    element: <Suspending><NotificationList /></Suspending> },
  { path: '/settings',         element: <Suspending><SettingsPage /></Suspending> },
  { path: '/follows/:followId',element: <Suspending><FollowPage /></Suspending> },
  { path: '/saved',            element: <Suspending><BookmarksPage /></Suspending> },
  { path: '/invite',           element: <Suspending><InvitePage /></Suspending> },
  { path: '/store',            element: <Suspending><StorePage /></Suspending> },
  { path: '/posts/:postId',    element: <Suspending><PostPage /></Suspending> },
  { path: '/welcome',          element: <Suspending><WelcomePage /></Suspending> },
  { path: '/about',            element: <Suspending><AboutPage /></Suspending> },
  { path: '/graveyard',        element: <Suspending><GraveyardPage /></Suspending> },
  { path: '/tomb/:hash',       element: <Suspending><TombPage /></Suspending> },
  { path: '/login',            element: <Suspending><LoginPanel /></Suspending> },
  { path: '/signup',           element: <Suspending><SignUpPage /></Suspending> },
  // /:username must be last — it catches everything else
  { path: '/:username',        element: <Suspending><UserPage /></Suspending> },
  { path: '*',                 element: <NotFoundPage /> },
];

/**
 * AppLayout — mounts the AppShell grid and all router-aware chrome.
 *
 * Everything routable lives inside the shell's `children` slot via
 * `<Outlet />`. Chrome that used to live in `Nav.tsx` (hamburger drawer,
 * tab bar, floating helpers) now either fills a dedicated AppShell slot
 * or renders as a sibling overlay — no more negative-margin Main hack
 * and no more `position: fixed` sidebar.
 *
 * The mobile drawer is owned here because the hamburger button lives in
 * TopBar, and TopBar is a slot prop of AppShell. Hoisting the state so
 * it's a plain React effect is simpler than threading a store through
 * both slots.
 */
function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <PowerHourBanner />
      <AppShell
        topBar={<TopBar onOpenDrawer={() => setDrawerOpen(true)} />}
        sideNav={<SideNav />}
        aside={<TrendingAside />}
        tabBar={<MobileTabBar inFlow />}
      >
        <Outlet />
      </AppShell>

      {/* Mobile/tablet navigation drawer — opens from the TopBar hamburger.
          Desktop (>= lg) uses the sticky SideNav in the shell instead. */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <SideNav variant="drawer" onNavigate={() => setDrawerOpen(false)} />
      </Drawer>

      <KonamiEasterEgg />
      <KeyboardShortcutsManager />
      <ScrollToTopButton />
      <MobileComposeFab />
      <ReferralClaimListener />
      <InstallPrompt />
      <FirstPostCelebration />
      <TimeDeltaFloater />
      <DeathWarningModal />
    </>
  );
}

const router = createBrowserRouter([
  // Layoutless routes — no navbar, no FAB, no chrome. Used for iframe
  // embeds on external sites.
  { path: '/embed/posts/:postId', element: <Suspending><EmbedPostPage /></Suspending> },
  {
    element: <AppLayout />,
    children: routes,
  },
]);

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <ColorModeProvider>
        <AuthProvider>
          <UserDataProvider>
            <NotificationsProvider>
              <FollowsProvider>
                <CompatibilityProvider>
                  <CacheProvider>
                    <LoadingProvider>
                      <RouterProvider router={router} />
                      <CRTEffect />
                    </LoadingProvider>
                  </CacheProvider>
                </CompatibilityProvider>
              </FollowsProvider>
            </NotificationsProvider>
          </UserDataProvider>
        </AuthProvider>
      </ColorModeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
